/**
 * Logbook Template Repository
 * 
 * Data access layer for logbook templates
 * Manages customizable logbook forms for different ship types
 * 
 * @module repositories/LogbookTemplateRepository
 */

import { BaseRepository, QueryOptions, QueryResult } from '../BaseRepository';
import { db } from '../db';
import type { LogbookTemplate, LogbookType } from '../../../types/logbook';

export class LogbookTemplateRepository extends BaseRepository<LogbookTemplate> {
  constructor() {
    super(db.logbook_templates, 'logbook_templates');
  }

  // ============================================================================
  // BASIC QUERIES
  // ============================================================================

  /**
   * Find templates by organization
   */
  async findByOrganization(organizationId: string, options?: QueryOptions): Promise<QueryResult<LogbookTemplate>> {
    return await this.findAll({
      ...options,
      filters: { ...options?.filters, organizationId }
    });
  }

  /**
   * Find templates by logbook type
   */
  async findByType(
    organizationId: string,
    logbookType: LogbookType
  ): Promise<LogbookTemplate[]> {
    return await this.table
      .where('[organizationId+logbookType]')
      .equals([organizationId, logbookType])
      .toArray();
  }

  /**
   * Find templates by ship type
   */
  async findByShipType(
    organizationId: string,
    shipType: string
  ): Promise<LogbookTemplate[]> {
    return await this.table
      .where('organizationId')
      .equals(organizationId)
      .and(template => template.shipType === shipType)
      .toArray();
  }

  /**
   * Get default template for a logbook type
   */
  async getDefault(
    organizationId: string,
    logbookType: LogbookType,
    shipType?: string
  ): Promise<LogbookTemplate | undefined> {
    const query = this.table
      .where('[organizationId+logbookType]')
      .equals([organizationId, logbookType])
      .and(template => template.isDefault === true);

    const templates = await query.toArray();

    // If ship type specified, prefer ship-type-specific template
    if (shipType) {
      const shipSpecific = templates.find(t => t.shipType === shipType);
      if (shipSpecific) return shipSpecific;
    }

    // Otherwise, return generic default (no ship type)
    return templates.find(t => !t.shipType) || templates[0];
  }

  // ============================================================================
  // ADVANCED QUERIES
  // ============================================================================

  /**
   * Find all templates for a specific logbook type and ship type combination
   */
  async findByTypeAndShipType(
    organizationId: string,
    logbookType: LogbookType,
    shipType: string
  ): Promise<LogbookTemplate[]> {
    return await this.table
      .where('organizationId')
      .equals(organizationId)
      .and(template => 
        template.logbookType === logbookType &&
        (template.shipType === shipType || !template.shipType)
      )
      .toArray();
  }

  /**
   * Search templates by name
   */
  async search(organizationId: string, searchTerm: string): Promise<LogbookTemplate[]> {
    const term = searchTerm.toLowerCase();

    return await this.table
      .where('organizationId')
      .equals(organizationId)
      .filter(template => template.templateName.toLowerCase().includes(term))
      .toArray();
  }

  // ============================================================================
  // TEMPLATE OPERATIONS
  // ============================================================================

  /**
   * Create template with validation
   */
  async createTemplate(data: Omit<LogbookTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<LogbookTemplate> {
    // If this is a default template, unset other defaults for same type/ship
    if (data.isDefault) {
      await this.unsetOtherDefaults(data.organizationId, data.logbookType, data.shipType);
    }

    const now = new Date();
    const template: LogbookTemplate = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now
    };

    await this.table.add(template);
    return template;
  }

  /**
   * Update template with validation
   */
  async updateTemplate(id: string, data: Partial<LogbookTemplate>): Promise<LogbookTemplate> {
    const existing = await this.findById(id);
    if (!existing) throw new Error('Template not found');

    // If setting as default, unset other defaults
    if (data.isDefault && !existing.isDefault) {
      await this.unsetOtherDefaults(
        existing.organizationId,
        existing.logbookType,
        existing.shipType
      );
    }

    return await this.update(id, data);
  }

  /**
   * Clone template
   */
  async clone(id: string, newName: string): Promise<LogbookTemplate> {
    const original = await this.findById(id);
    if (!original) throw new Error('Template not found');

    const cloned: Omit<LogbookTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
      organizationId: original.organizationId,
      templateName: newName,
      logbookType: original.logbookType,
      shipType: original.shipType,
      isDefault: false, // Clone is never default
      fieldsConfig: JSON.parse(JSON.stringify(original.fieldsConfig)), // Deep clone
      validationRules: JSON.parse(JSON.stringify(original.validationRules)),
      frequency: original.frequency,
      createdBy: original.createdBy
    };

    return await this.createTemplate(cloned);
  }

  /**
   * Set template as default
   */
  async setAsDefault(id: string): Promise<LogbookTemplate> {
    const template = await this.findById(id);
    if (!template) throw new Error('Template not found');

    // Unset other defaults
    await this.unsetOtherDefaults(
      template.organizationId,
      template.logbookType,
      template.shipType
    );

    return await this.update(id, { isDefault: true });
  }

  /**
   * Unset other default templates
   */
  private async unsetOtherDefaults(
    organizationId: string,
    logbookType: LogbookType,
    shipType?: string
  ): Promise<void> {
    const defaults = await this.table
      .where('organizationId')
      .equals(organizationId)
      .and(template =>
        template.logbookType === logbookType &&
        template.shipType === shipType &&
        template.isDefault === true
      )
      .toArray();

    for (const template of defaults) {
      await this.update(template.id, { isDefault: false });
    }
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  /**
   * Import templates from JSON
   */
  async importTemplates(jsonData: string): Promise<{ imported: number; errors: string[] }> {
    const errors: string[] = [];
    let imported = 0;

    try {
      const data = JSON.parse(jsonData);
      const templates = Array.isArray(data) ? data : [data];

      for (const template of templates) {
        try {
          await this.createTemplate(template);
          imported++;
        } catch (error) {
          errors.push(`Failed to import template: ${(error as Error).message}`);
        }
      }
    } catch (error) {
      errors.push(`JSON parsing failed: ${(error as Error).message}`);
    }

    return { imported, errors };
  }

  /**
   * Export templates to JSON
   */
  async exportTemplates(organizationId: string): Promise<string> {
    const templates = await this.table
      .where('organizationId')
      .equals(organizationId)
      .toArray();

    return JSON.stringify(templates, null, 2);
  }
}

export const logbookTemplateRepository = new LogbookTemplateRepository();

